class FeatureEnablementCapability {
  constructor(planStateSetters, planGetters, savePlan) {
    this.setters = planStateSetters;
    this.getters = planGetters;
    this.savePlan = savePlan;
  }

  async handleEnableFeature(action) {
    console.log("🎯 FeatureEnablementCapability: Feature enablement started:", action);
    
    let targetData = action;
    
    if (action.data) {
      targetData = action.data;
    }

    if (action.type === "component_capability" && action.data?.capability === "enable_feature") {
      targetData = action.data.data;
    }

    const featureId = targetData.featureId || targetData.feature_id;
    const { programs: proposedPrograms, triggerWorkflow } = targetData;

    if (!featureId) {
      console.error("❌ FeatureEnablementCapability: No featureId provided");
      return {
        success: false,
        message: "No feature ID provided for enablement",
        error: true,
      };
    }

    console.log(`🎯 Enabling feature: ${featureId}`);
    console.log(`🎯 Proposed programs count: ${proposedPrograms?.length || 0}`);

    const existingFeatures = Array.isArray(this.getters.enabledFeatures) 
      ? this.getters.enabledFeatures 
      : [];
    const updatedEnabledFeatures = [...new Set([...existingFeatures, featureId])];
    console.log(`🎯 Enabled features: ${existingFeatures.join(', ')} → ${updatedEnabledFeatures.join(', ')}`);

    this.setters.setEnabledFeatures(updatedEnabledFeatures);
    
    // FIX #31: Also update uiConfig.enabledFeatures so navigation tabs update immediately
    if (this.setters.setUIConfig && typeof this.setters.setUIConfig === 'function') {
      this.setters.setUIConfig(prev => {
        if (!prev) return prev;
        console.log("🎯 Updating uiConfig.enabledFeatures for navigation tabs:", updatedEnabledFeatures);
        return {
          ...prev,
          enabledFeatures: updatedEnabledFeatures
        };
      });
    }

    let updatedProgramSequence = [];
    let updatedSelectedPrograms = [];
    let newTotalDuration = 0;
    let programsAdded = 0;

    if (proposedPrograms && Array.isArray(proposedPrograms) && proposedPrograms.length > 0) {
      console.log(`🎯 Merging ${proposedPrograms.length} programs into plan`);

      const existingFinalSequence = Array.isArray(this.getters.finalProgramSequence) 
        ? this.getters.finalProgramSequence 
        : [];
      
      const existingSelectedPrograms = Array.isArray(this.getters.selectedPrograms)
        ? this.getters.selectedPrograms
        : [];
      
      updatedProgramSequence = this.mergeProgramsByName(existingFinalSequence, proposedPrograms);
      updatedSelectedPrograms = this.mergeProgramsByName(existingSelectedPrograms, proposedPrograms);
      
      programsAdded = updatedProgramSequence.length - existingFinalSequence.length;
      
      newTotalDuration = updatedProgramSequence.reduce((sum, program) => 
        sum + (program.totalProgramLength || program.duration || 0), 0
      );
      
      console.log(`🎯 FinalProgramSequence merged: ${existingFinalSequence.length} → ${updatedProgramSequence.length} (${programsAdded} new)`);
      console.log(`🎯 SelectedPrograms merged: ${existingSelectedPrograms.length} → ${updatedSelectedPrograms.length}`);
      console.log(`🎯 Total duration: ${newTotalDuration}`);

      this.setters.setFinalProgramSequence(updatedProgramSequence);
      this.setters.setSelectedPrograms(updatedSelectedPrograms);
      this.setters.setTotalDuration(newTotalDuration);
    } else {
      const existingPrograms = Array.isArray(this.getters.finalProgramSequence) 
        ? this.getters.finalProgramSequence 
        : [];
      updatedProgramSequence = existingPrograms;
      newTotalDuration = existingPrograms.reduce((sum, program) => 
        sum + (program.totalProgramLength || program.duration || 0), 0
      );
    }

    this.setters.setIsSaved(false);
    console.log("🎯 Plan marked as unsaved");

    // REVERTED: Just call savePlan() with no arguments - uses hydrated state
    // The delay gives React time to update state from the setters above
    setTimeout(() => {
      console.log("🎯 Triggering delayed save after feature enablement");
      if (this.savePlan && typeof this.savePlan === 'function') {
        // Pass only enabledFeatures in workflowContext, let savePlan use current hydrated state
        this.savePlan(null, { enabledFeatures: updatedEnabledFeatures });
        console.log("🎯 savePlan called with enabledFeatures:", updatedEnabledFeatures);
      }
    }, 500);

    // Agent-driven workflow trigger: if triggerWorkflow is true, set pending trigger for the feature
    if (triggerWorkflow && this.setters.setPendingFeatureWorkflowTrigger) {
      console.log(`🤖 Agent-driven trigger: Setting pendingFeatureWorkflowTrigger to "${featureId}"`);
      // Delay to allow React state to settle after feature enablement
      setTimeout(() => {
        this.setters.setPendingFeatureWorkflowTrigger(featureId);
      }, 600);
    }

    const result = {
      success: true,
      message: `Successfully enabled ${featureId}${triggerWorkflow ? ' and triggered workflow' : ''}`,
      featureId,
      enabledFeatures: updatedEnabledFeatures,
      programsAdded,
      totalPrograms: updatedProgramSequence.length,
      newTotalDuration,
      workflowTriggered: !!triggerWorkflow,
    };

    console.log(`🎯 ✅ Feature enablement complete:`, result);
    return result;
  }

  mergeProgramsByName(existingPrograms, newPrograms) {
    const mergedMap = new Map();
    
    existingPrograms.forEach(program => {
      const name = program.program_of_work || program.name;
      if (name) {
        mergedMap.set(name, program);
      }
    });

    newPrograms.forEach(program => {
      const name = program.program_of_work || program.name;
      if (name) {
        if (mergedMap.has(name)) {
          const existing = mergedMap.get(name);
          mergedMap.set(name, this.mergeProgram(existing, program));
          console.log(`🎯 Merged existing program: ${name}`);
        } else {
          // FIX: Ensure new programs have isIncluded: true and proper durations
          const programWithDefaults = {
            ...program,
            isIncluded: true, // Always enable new programs by default
          };
          
          // Ensure stage durations are calculated from tools
          if (programWithDefaults.toolsByStage) {
            Object.keys(programWithDefaults.toolsByStage).forEach(stage => {
              const stageData = programWithDefaults.toolsByStage[stage];
              if (stageData.tools && Array.isArray(stageData.tools)) {
                stageData.totalDuration = stageData.tools.reduce((sum, t) => sum + (t.duration || 0), 0);
              }
            });
            
            // Recalculate total program length
            programWithDefaults.totalProgramLength = Object.values(programWithDefaults.toolsByStage).reduce(
              (sum, stageData) => sum + (stageData.totalDuration || 0), 0
            );
          }
          
          mergedMap.set(name, programWithDefaults);
          console.log(`🎯 Added new program: ${name} (isIncluded: ${programWithDefaults.isIncluded}, duration: ${programWithDefaults.totalProgramLength})`);
        }
      }
    });

    return Array.from(mergedMap.values());
  }

  mergeProgram(existing, incoming) {
    const merged = { ...existing };
    
    // FIX: Always ensure isIncluded is true (programs should be enabled by default)
    merged.isIncluded = true;
    
    if (incoming.toolsByStage) {
      merged.toolsByStage = { ...(existing.toolsByStage || {}) };
      
      Object.entries(incoming.toolsByStage).forEach(([stage, stageData]) => {
        if (!merged.toolsByStage[stage]) {
          merged.toolsByStage[stage] = { tools: [], totalDuration: 0 };
        }
        
        const existingToolNames = new Set(
          merged.toolsByStage[stage].tools.map(t => t.tool || t.name)
        );
        
        const newTools = (stageData.tools || []).filter(
          t => !existingToolNames.has(t.tool || t.name)
        );
        
        merged.toolsByStage[stage].tools = [
          ...merged.toolsByStage[stage].tools,
          ...newTools
        ];
        
        // FIX: Recalculate totalDuration for each stage from tool durations
        merged.toolsByStage[stage].totalDuration = merged.toolsByStage[stage].tools.reduce(
          (sum, t) => sum + (t.duration || 0), 0
        );
        
        console.log(`🎯 Stage "${stage}" duration: ${merged.toolsByStage[stage].totalDuration} (${merged.toolsByStage[stage].tools.length} tools)`);
      });
    }

    // FIX: Recalculate total program length from all stage durations
    merged.totalProgramLength = Object.values(merged.toolsByStage || {}).reduce(
      (sum, stageData) => sum + (stageData.totalDuration || 0), 0
    );
    
    console.log(`🎯 Program "${merged.program_of_work}" total duration: ${merged.totalProgramLength}`);

    if (incoming.Stages && Array.isArray(incoming.Stages)) {
      const existingStages = new Set(merged.Stages || []);
      incoming.Stages.forEach(stage => existingStages.add(stage));
      merged.Stages = Array.from(existingStages);
    }

    return merged;
  }
}

module.exports = FeatureEnablementCapability;
